import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Download,
  FileDown,
  Info,
  MinusCircle,
  RefreshCw,
  ShieldAlert
} from 'lucide-react';
import { Button } from 'components/ui/Button';
import { ConfirmationOverlay } from 'components/feedback/ConfirmationOverlay/ConfirmationOverlay';
import { GenericLoading } from 'components/ui/Loader';
import { useAbp } from 'core/hooks/useAbp';
import { AbpPolicies } from 'core/constants/AbpPolicies';
import {
  confirmHbsUpload,
  downloadHbsRemise,
  generateHbsRemise,
  getHbsRemises,
  triggerBlobDownload,
  GetHbsRemisesResponse,
  HbsRemiseListItem
} from './api/hbsPrelevementsApi';

/** ISO datetime -> jj/mm/aaaa. */
const toFrDate = (iso: string): string => {
  const [y, m, d] = iso.slice(0, 10).split('-');
  return y && m && d ? `${d}/${m}/${y}` : iso;
};

/** ISO datetime -> jj/mm/aaaa hh:mm (heure locale). */
const toFrDateTime = (iso: string): string =>
  new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });

const StatusBadge: React.FC<{ remise: HbsRemiseListItem }> = ({ remise }) =>
  // 0 affaire nouvelle : rien à déposer sur HBS, ni attente ni upload
  remise.contractsCount === 0 ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-500">
      <MinusCircle className="h-3.5 w-3.5" />
      Rien à transmettre
    </span>
  ) : remise.isUploaded ? (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800"
      title={
        remise.uploadConfirmedAt
          ? `Upload confirmé le ${toFrDateTime(remise.uploadConfirmedAt)}`
          : undefined
      }
    >
      <CheckCircle2 className="h-3.5 w-3.5" />
      Uploadé
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2.5 py-0.5 text-xs font-medium text-orange-800">
      <Clock className="h-3.5 w-3.5" />
      En attente d'upload
    </span>
  );

export const HbsPrelevementsView: React.FC = () => {
  const { hasPermission } = useAbp();
  const canRead = hasPermission(AbpPolicies.PRELEVEMENTS_HBS_READ);
  const canManage = hasPermission(AbpPolicies.PRELEVEMENTS_HBS_MANAGE);

  const [data, setData] = useState<GetHbsRemisesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [isGenerating, setIsGenerating] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  /** Remise en attente de confirmation « J'ai uploadé » (modale anti miss-click). */
  const [remiseToConfirm, setRemiseToConfirm] = useState<HbsRemiseListItem | null>(null);
  /** Clé "action:id-de-remise" de la ligne en cours de traitement. */
  const [busyRowAction, setBusyRowAction] = useState<string | null>(null);

  // La génération est portée par un job planifié (le 5 du mois à 4h) : le bouton manuel
  // est caché. Pour les tests / rattrapages : 5 clics rapides sur le titre l'affichent.
  const [showGenerateButton, setShowGenerateButton] = useState(false);
  const titleClicksRef = useRef<{ count: number; lastAt: number }>({ count: 0, lastAt: 0 });

  const handleTitleClick = useCallback(() => {
    if (showGenerateButton) return;
    const now = Date.now();
    const clicks = titleClicksRef.current;
    clicks.count = now - clicks.lastAt < 2000 ? clicks.count + 1 : 1;
    clicks.lastAt = now;
    if (clicks.count >= 5) {
      setShowGenerateButton(true);
      toast.success('Mode test activé : bouton de génération visible.');
    } else if (clicks.count >= 3) {
      toast.info(`Encore ${5 - clicks.count} clic(s) pour activer le mode test.`, {
        autoClose: 1500
      });
    }
  }, [showGenerateButton]);

  const fetchRemises = useCallback(async () => {
    setLoadError(null);
    try {
      setData(await getHbsRemises());
    } catch (error: any) {
      setLoadError(error?.message || 'Erreur lors du chargement des remises HBS.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canRead) {
      fetchRemises();
    } else {
      setIsLoading(false);
    }
  }, [canRead, fetchRemises]);

  const handleGenerate = useCallback(async () => {
    setIsGenerating(true);
    try {
      // Le POST renvoie du JSON ; le fichier se télécharge via GET /remises/{id}/csv.
      const { generationId, contractsCount } = await generateHbsRemise();
      if (contractsCount > 0) {
        const { blob, filename } = await downloadHbsRemise(generationId);
        triggerBlobDownload(blob, filename);
        toast.success(`Remise générée : ${contractsCount} contrat(s).`);
      } else {
        toast.info('Aucune affaire nouvelle — remise tracée sans contrat.');
      }
      // Une ligne apparaît dans le tableau même à 0 contrat.
      await fetchRemises();
    } catch (error: any) {
      toast.error(error?.message || 'Erreur lors de la génération de la remise HBS.');
    } finally {
      setIsGenerating(false);
    }
  }, [fetchRemises]);

  const handleDownload = useCallback(async (remise: HbsRemiseListItem) => {
    setBusyRowAction(`download:${remise.id}`);
    try {
      const { blob, filename } = await downloadHbsRemise(remise.id);
      triggerBlobDownload(blob, filename);
    } catch (error: any) {
      toast.error(error?.message || 'Impossible de récupérer la remise HBS.');
    } finally {
      setBusyRowAction(null);
    }
  }, []);

  const handleConfirmUpload = useCallback(
    async (remise: HbsRemiseListItem) => {
      setBusyRowAction(`confirm:${remise.id}`);
      try {
        const res = await confirmHbsUpload(remise.id);
        if (res.isSuccess) {
          // Le toaster de succès (res.notification) est affiché automatiquement par l'interceptor ;
          // fallback si le back ne renvoie pas de notification.
          if (!res.notification) {
            toast.success(
              `Upload confirmé pour la remise du ${toFrDate(remise.generationDate)}.`
            );
          }
          await fetchRemises();
        } else {
          toast.error(res.errorMessage || "Échec de la confirmation d'upload HBS.");
        }
      } catch (error: any) {
        toast.error(error?.message || "Échec de la confirmation d'upload HBS.");
      } finally {
        setBusyRowAction(null);
      }
    },
    [fetchRemises]
  );

  if (!canRead) {
    return (
      <div className="mx-auto max-w-3xl py-16 text-center">
        <ShieldAlert className="mx-auto h-10 w-10 text-red-400" />
        <h2 className="mt-4 text-lg font-semibold text-gray-900">Accès non autorisé</h2>
        <p className="mt-1 text-sm text-gray-500">
          Vous n'avez pas la permission d'accéder aux prélèvements HBS.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl py-6">
      {/* Bandeau garde-fou hors production — non masquable */}
      {data && !data.isProduction && (
        <div className="mb-6 rounded-xl border-2 border-red-400 bg-red-50 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-6 w-6 flex-shrink-0 text-red-600" />
            <div className="text-sm text-red-900">
              <p className="text-base font-bold">
                ⚠️ Environnement {data.environment} — ne déposez jamais ces fichiers sur
                le portail HBS de production.
              </p>
              <p className="mt-1">
                Les fichiers sont préfixés{' '}
                <strong>NE-PAS-UPLOADER-{data.environment.toUpperCase()}</strong>.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          {/* 5 clics rapides sur le titre = affiche le bouton de génération manuelle (tests) */}
          <h2
            className="select-none text-xl font-bold text-gray-900"
            onClick={handleTitleClick}
          >
            Prélèvements HBS
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Remises CSV des affaires nouvelles animaux (PR_ANI), à déposer sur le portail
            bancaire HBS.
          </p>
        </div>
        {canManage && showGenerateButton && (
          <Button color="primary" onClick={() => setIsConfirmOpen(true)} disabled={isGenerating}>
            <FileDown data-slot="icon" />
            {isGenerating ? 'Génération…' : 'Générer la remise'}
          </Button>
        )}
      </div>

      {/* Consignes / workflow */}
      <div className="mb-6 rounded-xl border border-blue-200 bg-blue-50 p-4">
        <div className="flex items-start gap-2">
          <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" />
          <div className="text-sm text-blue-900">
            <p className="font-semibold">
              Workflow : la remise est générée automatiquement le 5 du mois → télécharger
              le fichier → le déposer sur HBS → « J'ai uploadé sur HBS ».
            </p>
            <p className="mt-1">
              Le fichier téléchargé s'uploade sur HBS <strong>tel quel</strong> — ne pas
              l'ouvrir / ré-enregistrer dans Excel (cela casserait le format exigé par HBS).
            </p>
          </div>
        </div>
      </div>

      {/* Tableau des remises */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {isLoading ? (
          <div className="p-10">
            <GenericLoading loading isParentSized />
          </div>
        ) : loadError ? (
          <div className="flex flex-col items-center gap-3 p-10 text-center">
            <p className="text-sm text-red-600">{loadError}</p>
            <Button color="slate" onClick={() => { setIsLoading(true); fetchRemises(); }}>
              <RefreshCw data-slot="icon" />
              Réessayer
            </Button>
          </div>
        ) : !data || data.remises.length === 0 ? (
          <p className="p-10 text-center text-sm text-gray-500">
            Aucune remise HBS générée pour le moment.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Générée le
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Affaires nouvelles
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Statut
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.remises.map((remise) => (
                  <tr key={remise.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">
                      {toFrDateTime(remise.generationDate)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                      {remise.contractsCount > 0 ? (
                        remise.contractsCount
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-500">
                          Aucune
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <StatusBadge remise={remise} />
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      {/* Remise à 0 contrat : rien à déposer, aucune action */}
                      {remise.contractsCount > 0 && (
                        <div className="flex items-center justify-end gap-2">
                          {canManage && (
                            <Button
                              color="slate"
                              onClick={() => handleDownload(remise)}
                              disabled={busyRowAction === `download:${remise.id}`}
                            >
                              <Download data-slot="icon" />
                              {busyRowAction === `download:${remise.id}`
                                ? 'Téléchargement…'
                                : 'Télécharger'}
                            </Button>
                          )}
                          {remise.isUploaded ? (
                            remise.uploadConfirmedAt && (
                              <span className="text-xs text-gray-500">
                                Confirmé le {toFrDateTime(remise.uploadConfirmedAt)}
                              </span>
                            )
                          ) : (
                            canManage && (
                              <Button
                                color="primary"
                                onClick={() => setRemiseToConfirm(remise)}
                                disabled={busyRowAction === `confirm:${remise.id}`}
                              >
                                {busyRowAction === `confirm:${remise.id}`
                                  ? 'Confirmation…'
                                  : "J'ai uploadé sur HBS"}
                              </Button>
                            )
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmationOverlay
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleGenerate}
        title="Générer la remise HBS ?"
        description="Les contrats inclus dans cette remise seront marqués comme transmis."
        confirmLabel="Générer"
        variant="warning"
        isLoading={isGenerating}
      />

      {/* Anti miss-click : la confirmation marque la remise uploadée (et stoppera les rappels) */}
      <ConfirmationOverlay
        isOpen={!!remiseToConfirm}
        onClose={() => setRemiseToConfirm(null)}
        onConfirm={() => remiseToConfirm && handleConfirmUpload(remiseToConfirm)}
        title="Confirmer l'upload sur HBS ?"
        description={
          remiseToConfirm
            ? `Vous confirmez que le fichier de la remise du ${toFrDate(
                remiseToConfirm.generationDate
              )} (${remiseToConfirm.contractsCount} contrat(s)) a bien été déposé sur le portail HBS. Cette action marque la remise comme uploadée.`
            : ''
        }
        confirmLabel="Oui, c'est uploadé"
        variant="warning"
      />
    </div>
  );
};
